import { AccountType } from "@/App";
import { createContext, useContext, useState } from "react";

export interface Load {
  id: string;
  source: string;
  pickupStart: string;
  pickupEnd: string;
  destination: string;
  deliveryStart: string;
  deliveryEnd: string;
  weight: number;
  pieces: number;

  createdAt: string;
  assignedAt: string;
  carrierCost: number;
  distributorCost: number;
  status:
    | "Not Assigned"
    | "Assigned"
    | "Being Picked Up"
    | "In Transit"
    | "Delivered"
    | "Cancelled";
  carrierID?: string; // Optional, only if assigned
  driverName?: string; // Optional, only if assigned
  driverCdl?: string; // Optional, only if assigned
  driverPhone?: string; // Optional, only if assigned
  truckId?: string; // Optional, only if assigned
  driverLicenseNumber?: string; // Optional, only if assigned
  truckVinLast6?: string; // Optional, only if assigned
  truckLicensePlate?: string; // Optional, only if assigned
  trailerNumber?: string; // Optional, only if assigned
  trailerLicensePlate?: string; // Optional, only if assigned
}

export interface Invoice {
  loads: Load[];
  totalAmount: number;
  dateCreated: Date;
  dateDue: Date;
  status: "Pending" | "Paid" | "Overdue" | "Refunded";
  billFromOrgId: string;
  billToOrgId: string;
  invoiceName: string;
}
export interface Carrier {
  id: string;
  name: string;
  rating: number;
  totalLoads: number;
  location: string;
  contact: {
    phone: string;
    email: string;
    address: string;
    contactPerson: string;
    title: string;
  };
  createdAt: string;
}
export interface Truck {
  id: string;
  driver: string;
  carrierId: string;
  model: string;
  year: number;
  status: "Active" | "Loading" | "Maintenance" | "Inactive";
  type: "Flatbed" | "Dry Van" | "Reefer" | "Box Truck";
  maxCapacity: number;
  maxWeight: number;
  //   createdAt: string;
}

export interface ClaimDetail {
  id: string;
  claimId: string;
  date: Date;
  message: string;
}

export interface Claim {
  id: string;
  state: "open" | "closed";
  submittedTo: string | null;
  createdAt: Date;
  createdBy: string;
  loadId: string;
  claimAmount: number;
}

interface Driver {
  id: string;
  name: string;
  currentTruck: string;
  contact: number;
}

interface DemoData {
  loads: Load[];
  invoices: Invoice[];
  carriers: Carrier[];
  trucks: Truck[];
  drivers: Driver[];
  claims: Claim[];
  claimDetails: ClaimDetail[];
  accountType: AccountType;
  setAccountType: (accountType: AccountType) => void;
  addDriver: (driver: Driver) => void;
  updateDriver: (id: string, driver: Partial<Driver>) => void;
  addLoad: (load: Load) => void;
  addCarrier: (carrier: Carrier) => void;
  addTruck: (truck: Truck) => void;
  updateLoad: (id: string, load: Partial<Load>) => void;
  updateCarrier: (id: string, carrier: Partial<Carrier>) => void;
  updateTruck: (id: string, truck: Partial<Truck>) => void;
  addClaim: (claim: Claim) => void;
  addClaimDetails: (detail: ClaimDetail) => void;
  updateClaim: (id: string, claim: Partial<Claim>) => void;
  updateClaimDetails: (id: string, detail: Partial<ClaimDetail>) => void;
  addInvoice: (invoice: Invoice) => void;
  updateInvoice: (id: string, invoice: Partial<Invoice>) => void;
}

const DemoData = createContext({
  loads: [],
  invoices: [],
  carriers: [],
  bids: [],
  trucks: [],
  drivers: [],
  claims: [],
  claimDetails: [],
  accountType: "distributor",
  setAccountType: (accountType: AccountType) => {},
  addDriver: (driver: Driver) => {},
  updateDriver: (id: string, driver: Partial<Driver>) => {},
  addLoad: (load: Load) => {},
  addCarrier: (carrier: Carrier) => {},
  addTruck: (truck: Truck) => {},
  updateLoad: (id: string, load: Partial<Load>) => {},
  updateCarrier: (id: string, carrier: Partial<Carrier>) => {},
  updateTruck: (id: string, truck: Partial<Truck>) => {},
  addClaim: (claim: Claim) => {},
  addClaimDetails: (detail: ClaimDetail) => {},
  updateClaim: (id: string, claim: Partial<Claim>) => {},
  updateClaimDetails: (id: string, detail: Partial<ClaimDetail>) => {},
  addInvoice: (invoice: Invoice) => {},
  updateInvoice: (id: string, invoice: Partial<Invoice>) => {},
} as DemoData);

export const useDemoData = () => useContext(DemoData);

export function DemoDataProvider({ children }: { children: React.ReactNode }) {
  const [claims, setClaims] = useState<Claim[]>([
    {
      id: "CLAIM001",
      state: "open",
      submittedTo: null,
      createdAt: new Date(),
      createdBy: "distributor",
      loadId: "LOAD001",
      claimAmount: 500,
    },
    {
      id: "CLAIM002",
      state: "open",
      submittedTo: null,
      createdAt: new Date(),
      createdBy: "distributor",
      loadId: "LOAD002",
      claimAmount: 300,
    },
  ]);
  const [claimDetails, setClaimDetails] = useState<ClaimDetail[]>([
    {
      id: "1",
      claimId: "CLAIM001",
      date: new Date(),
      message: "Initial claim created.",
    },
    {
      id: "2",
      claimId: "CLAIM001",
      date: new Date(),
      message: "Claim under review.",
    },
    {
      id: "3",
      claimId: "CLAIM002",
      date: new Date(),
      message: "Initial claim created.",
    },
  ]);
  const [loads, setLoads] = useState<Load[]>([
    // Assigned Loads
    {
      id: "LOAD001",
      source: "Chicago, IL",
      pickupStart: "2024-01-15T08:00:00Z",
      pickupEnd: "2024-01-15T12:00:00Z",
      destination: "Houston, TX",
      deliveryStart: "2024-01-17T08:00:00Z",
      deliveryEnd: "2024-01-17T17:00:00Z",
      weight: 45000,
      pieces: 24,
      carrierID: "CAR001",
      carrierCost: 1500,
      distributorCost: 1800,
      createdAt: "2024-01-10T14:30:00Z",
      assignedAt: "2024-01-12T09:15:00Z",
      status: "Assigned",
    },
    {
      id: "LOAD002",
      source: "Miami, FL",
      pickupStart: "2024-01-16T10:00:00Z",
      pickupEnd: "2024-01-16T14:00:00Z",
      destination: "New York, NY",
      deliveryStart: "2024-01-19T08:00:00Z",
      deliveryEnd: "2024-01-19T16:00:00Z",
      weight: 38000,
      pieces: 18,
      carrierID: "CAR002",
      carrierCost: 1200,
      distributorCost: 1500,
      createdAt: "2024-01-11T11:20:00Z",
      assignedAt: "2024-01-13T15:45:00Z",
      status: "Assigned",
    },
    {
      id: "LOAD003",
      source: "Seattle, WA",
      pickupStart: "2024-01-18T07:00:00Z",
      pickupEnd: "2024-01-18T11:00:00Z",
      destination: "Phoenix, AZ",
      deliveryStart: "2024-01-21T09:00:00Z",
      deliveryEnd: "2024-01-21T15:00:00Z",
      weight: 52000,
      pieces: 32,
      carrierID: "CAR003",
      carrierCost: 2000,
      distributorCost: 2400,
      createdAt: "2024-01-12T16:10:00Z",
      assignedAt: "2024-01-14T10:30:00Z",
      status: "Assigned",
    },
    {
      id: "LOAD004",
      source: "Portland, OR",
      pickupStart: "2024-01-20T09:00:00Z",
      pickupEnd: "2024-01-20T13:00:00Z",
      destination: "Las Vegas, NV",
      deliveryStart: "2024-01-22T10:00:00Z",
      deliveryEnd: "2024-01-22T18:00:00Z",
      weight: 28000,
      pieces: 15,
      carrierID: "CAR004",
      carrierCost: 900,
      distributorCost: 1100,
      createdAt: "2024-01-13T13:45:00Z",
      assignedAt: "2024-01-15T08:20:00Z",
      status: "Assigned",
    },
    {
      id: "LOAD005",
      source: "Portland, OR",
      pickupStart: "2024-01-20T09:00:00Z",
      pickupEnd: "2024-01-20T13:00:00Z",
      destination: "Las Vegas, NV",
      deliveryStart: "2024-01-22T10:00:00Z",
      deliveryEnd: "2024-01-22T18:00:00Z",
      weight: 28000,
      pieces: 15,
      carrierID: "CAR004",
      carrierCost: 900,
      distributorCost: 1100,
      createdAt: "2024-01-13T13:45:00Z",
      assignedAt: "2024-01-15T08:20:00Z",
      status: "Delivered",
    },

    // Unassigned Loads
    {
      id: "LOAD006",
      source: "Boston, MA",
      pickupStart: "2024-01-23T07:00:00Z",
      pickupEnd: "2024-01-23T11:00:00Z",
      destination: "Charlotte, NC",
      deliveryStart: "2024-01-25T08:00:00Z",
      deliveryEnd: "2024-01-25T16:00:00Z",
      weight: 35000,
      pieces: 19,
      carrierID: "",
      carrierCost: 1500,
      distributorCost: 1800,
      createdAt: "2024-01-17T14:22:00Z",
      assignedAt: "",
      status: "Not Assigned",
    },
    {
      id: "LOAD007",
      source: "Kansas City, MO",
      pickupStart: "2024-01-24T09:00:00Z",
      pickupEnd: "2024-01-24T13:00:00Z",
      destination: "Salt Lake City, UT",
      deliveryStart: "2024-01-26T10:00:00Z",
      deliveryEnd: "2024-01-26T18:00:00Z",
      weight: 48000,
      pieces: 28,
      carrierID: "",
      carrierCost: 2500,
      distributorCost: 3000,
      createdAt: "2024-01-18T09:30:00Z",
      assignedAt: "",
      status: "Not Assigned",
    },
    {
      id: "LOAD008",
      source: "Philadelphia, PA",
      pickupStart: "2024-01-25T08:00:00Z",
      pickupEnd: "2024-01-25T12:00:00Z",
      destination: "Jacksonville, FL",
      deliveryStart: "2024-01-27T09:00:00Z",
      deliveryEnd: "2024-01-27T17:00:00Z",
      weight: 33000,
      pieces: 16,
      carrierID: "",
      carrierCost: 1400,
      distributorCost: 1700,
      createdAt: "2024-01-19T11:45:00Z",
      assignedAt: "",
      status: "Not Assigned",
    },
    {
      id: "LOAD009",
      source: "Minneapolis, MN",
      pickupStart: "2024-01-26T07:00:00Z",
      pickupEnd: "2024-01-26T11:00:00Z",
      destination: "Oklahoma City, OK",
      deliveryStart: "2024-01-28T08:00:00Z",
      deliveryEnd: "2024-01-28T16:00:00Z",
      weight: 44000,
      pieces: 25,
      carrierID: "",
      carrierCost: 1800,
      distributorCost: 2200,
      createdAt: "2024-01-20T15:10:00Z",
      assignedAt: "",
      status: "Not Assigned",
    },
    {
      id: "LOAD010",
      source: "San Antonio, TX",
      pickupStart: "2024-01-27T10:00:00Z",
      pickupEnd: "2024-01-27T14:00:00Z",
      destination: "Albuquerque, NM",
      deliveryStart: "2024-01-29T09:00:00Z",
      deliveryEnd: "2024-01-29T15:00:00Z",
      weight: 39000,
      pieces: 21,
      carrierID: "",
      carrierCost: 1600,
      distributorCost: 2000,
      createdAt: "2024-01-21T12:25:00Z",
      assignedAt: "",
      status: "Not Assigned",
    },
  ]);
  const [carriers, setCarriers] = useState<Carrier[]>([
    {
      id: "CAR001",
      name: "Swift Transport Solutions",
      rating: 4.8,
      totalLoads: 1247,
      location: "Dallas, TX",
      contact: {
        phone: "(214) 555-0123",
        email: "dispatch@swifttransport.com",
        address: "1234 Industrial Blvd, Dallas, TX 75201",
        contactPerson: "Mike Johnson",
        title: "Operations Manager",
      },
      createdAt: "2022-03-15T08:30:00Z",
    },
    {
      id: "CAR002",
      name: "Reliable Freight Co",
      rating: 4.6,
      totalLoads: 892,
      location: "Atlanta, GA",
      contact: {
        phone: "(404) 555-0456",
        email: "operations@reliablefreight.com",
        address: "5678 Logistics Way, Atlanta, GA 30309",
        contactPerson: "Sarah Williams",
        title: "Fleet Coordinator",
      },
      createdAt: "2021-11-22T14:15:00Z",
    },
    {
      id: "CAR003",
      name: "Mountain Express Logistics",
      rating: 4.9,
      totalLoads: 2156,
      location: "Denver, CO",
      contact: {
        phone: "(303) 555-0789",
        email: "dispatch@mountainexpress.com",
        address: "9012 Highway 36, Denver, CO 80202",
        contactPerson: "David Rodriguez",
        title: "Dispatch Manager",
      },
      createdAt: "2020-07-08T10:45:00Z",
    },
    {
      id: "CAR004",
      name: "Coastal Carriers Inc",
      rating: 4.4,
      totalLoads: 634,
      location: "Long Beach, CA",
      contact: {
        phone: "(562) 555-0321",
        email: "info@coastalcarriers.com",
        address: "3456 Port Avenue, Long Beach, CA 90802",
        contactPerson: "Jennifer Chen",
        title: "Operations Director",
      },
      createdAt: "2023-01-12T16:20:00Z",
    },
  ]);

  const [trucks, setTrucks] = useState<Truck[]>([
    // Swift Transport Solutions trucks
    {
      id: "TRK001",
      driver: "John Smith",
      carrierId: "CAR001",
      model: "Freightliner Cascadia",
      year: 2021,
      status: "Loading",
      type: "Dry Van",
      maxCapacity: 3400,
      maxWeight: 80000,
    },
    {
      id: "TRK002",
      driver: "Maria Garcia",
      carrierId: "CAR001",
      model: "Peterbilt 579",
      year: 2020,
      status: "Active",
      type: "Flatbed",
      maxCapacity: 2800,
      maxWeight: 80000,
    },
    {
      id: "TRK003",
      driver: "Robert Brown",
      carrierId: "CAR001",
      model: "Kenworth T680",
      year: 2022,
      status: "Active",
      type: "Reefer",
      maxCapacity: 3200,
      maxWeight: 80000,
    },
    {
      id: "TRK004",
      driver: "Lisa Johnson",
      carrierId: "CAR001",
      model: "Volvo VNL",
      year: 2019,
      status: "Maintenance",
      type: "Dry Van",
      maxCapacity: 3400,
      maxWeight: 80000,
    },

    // Reliable Freight Co trucks
    {
      id: "TRK005",
      driver: "Michael Davis",
      carrierId: "CAR002",
      model: "Mack Anthem",
      year: 2021,
      status: "Active",
      type: "Flatbed",
      maxCapacity: 2800,
      maxWeight: 80000,
    },
    {
      id: "TRK006",
      driver: "Amanda Wilson",
      carrierId: "CAR002",
      model: "International LT",
      year: 2020,
      status: "Loading",
      type: "Dry Van",
      maxCapacity: 3400,
      maxWeight: 80000,
    },
    {
      id: "TRK007",
      driver: "Carlos Martinez",
      carrierId: "CAR002",
      model: "Freightliner Cascadia",
      year: 2022,
      status: "Active",
      type: "Reefer",
      maxCapacity: 3200,
      maxWeight: 80000,
    },
    {
      id: "TRK008",
      driver: "Jennifer Taylor",
      carrierId: "CAR002",
      model: "Peterbilt 389",
      year: 2018,
      status: "Active",
      type: "Flatbed",
      maxCapacity: 2800,
      maxWeight: 80000,
    },

    // Mountain Express Logistics trucks
    {
      id: "TRK009",
      driver: "Thomas Anderson",
      carrierId: "CAR003",
      model: "Kenworth W900",
      year: 2021,
      status: "Loading",
      type: "Flatbed",
      maxCapacity: 2800,
      maxWeight: 80000,
    },
    {
      id: "TRK010",
      driver: "Patricia Moore",
      carrierId: "CAR003",
      model: "Volvo VNL 860",
      year: 2022,
      status: "Active",
      type: "Dry Van",
      maxCapacity: 3400,
      maxWeight: 80000,
    },
    {
      id: "TRK011",
      driver: "James White",
      carrierId: "CAR003",
      model: "Freightliner Coronado",
      year: 2020,
      status: "Active",
      type: "Reefer",
      maxCapacity: 3200,
      maxWeight: 80000,
    },
    {
      id: "TRK012",
      driver: "Susan Clark",
      carrierId: "CAR003",
      model: "Mack Pinnacle",
      year: 2019,
      status: "Active",
      type: "Box Truck",
      maxCapacity: 1200,
      maxWeight: 26000,
    },
    {
      id: "TRK013",
      driver: "Kevin Lewis",
      carrierId: "CAR003",
      model: "International ProStar",
      year: 2021,
      status: "Maintenance",
      type: "Dry Van",
      maxCapacity: 3400,
      maxWeight: 80000,
    },

    // Coastal Carriers Inc trucks
    {
      id: "TRK014",
      driver: "Nancy Hall",
      carrierId: "CAR004",
      model: "Peterbilt 567",
      year: 2020,
      status: "Active",
      type: "Flatbed",
      maxCapacity: 2800,
      maxWeight: 80000,
    },
    {
      id: "TRK015",
      driver: "Daniel Young",
      carrierId: "CAR004",
      model: "Kenworth T880",
      year: 2021,
      status: "Loading",
      type: "Dry Van",
      maxCapacity: 3400,
      maxWeight: 80000,
    },
    {
      id: "TRK016",
      driver: "Michelle King",
      carrierId: "CAR004",
      model: "Volvo VHD",
      year: 2019,
      status: "Active",
      type: "Box Truck",
      maxCapacity: 1200,
      maxWeight: 26000,
    },
    {
      id: "TRK017",
      driver: "Christopher Wright",
      carrierId: "CAR004",
      model: "Freightliner M2",
      year: 2022,
      status: "Active",
      type: "Box Truck",
      maxCapacity: 1200,
      maxWeight: 26000,
    },
  ]);

  const [drivers, setDrivers] = useState<Driver[]>(
    trucks.map((truck) => {
      return {
        id: `${truck.id}-DRIVER`,
        name: truck.driver,
        currentTruck: truck.id,
        contact: Math.floor(Math.random() * 1000000000), // Random contact number for demo
      };
    })
  );

  const [accountType, setAccountType] = useState<AccountType>("supplier");

  const [invoices, setInvoices] = useState<Invoice[]>([
    {
      loads: [loads[0], loads[1]],
      totalAmount: 3300,
      dateCreated: new Date("2024-01-10"),
      dateDue: new Date("2024-02-10"),
      status: "Pending",
      billFromOrgId: "Cabellos Logistics",
      billToOrgId: "Transfire",
      invoiceName: "January Invoice",
    },
    {
      loads: [loads[2], loads[3]],
      totalAmount: 2900,
      dateCreated: new Date("2024-01-12"),
      dateDue: new Date("2024-02-12"),
      status: "Pending",
      billFromOrgId: "Capito Logistics",
      billToOrgId: "Transfire",
      invoiceName: "February Invoice",
    },
    {
      loads: [loads[4], loads[5]],
      totalAmount: 2100,
      dateCreated: new Date("2024-01-14"),
      dateDue: new Date("2024-02-14"),
      status: "Pending",
      billFromOrgId: "Capito Logistics",
      billToOrgId: "Transfire",
      invoiceName: "March Invoice",
    },
    {
      loads: [loads[0], loads[1]],
      totalAmount: 2500,
      dateCreated: new Date("2024-01-16"),
      dateDue: new Date("2024-02-16"),
      status: "Pending",
      billFromOrgId: "Transfire",
      billToOrgId: "Receiver",
      invoiceName: "April Invoice",
    },
    {
      loads: [loads[2], loads[3]],
      totalAmount: 3000,
      dateCreated: new Date("2024-01-18"),
      dateDue: new Date("2024-02-18"),
      status: "Pending",
      billFromOrgId: "Transfire",
      billToOrgId: "Receiver",
      invoiceName: "May Invoice",
    },
  ]);

  function addDriver(driver: Driver) {
    setDrivers((prev) => [...prev, driver]);
  }
  function updateDriver(driverId: string, updatedDriver: Partial<Driver>) {
    setDrivers((prev) =>
      prev.map((driver) =>
        driver.id === driverId ? { ...driver, ...updatedDriver } : driver
      )
    );
  }

  function addLoad(load: Load) {
    setLoads((prev) => [...prev, load]);
  }
  function addCarrier(carrier: Carrier) {
    setCarriers((prev) => [...prev, carrier]);
  }
  function addTruck(truck: Truck) {
    setTrucks((prev) => [...prev, truck]);
  }
  function addClaim(claim: Claim) {
    setClaims((prev) => [...prev, claim]);
  }
  function addClaimDetail(detail: ClaimDetail) {
    setClaimDetails((prev) => [...prev, detail]);
  }
  function updateLoad(loadId: string, updatedLoad: Partial<Load>) {
    setLoads((prev) =>
      prev.map((load) =>
        load.id === loadId ? { ...load, ...updatedLoad } : load
      )
    );
  }
  function updateCarrier(carrierId: string, updatedCarrier: Partial<Carrier>) {
    setCarriers((prev) =>
      prev.map((carrier) =>
        carrier.id === carrierId ? { ...carrier, ...updatedCarrier } : carrier
      )
    );
  }
  function updateTruck(truckId: string, updatedTruck: Partial<Truck>) {
    setTrucks((prev) =>
      prev.map((truck) =>
        truck.id === truckId ? { ...truck, ...updatedTruck } : truck
      )
    );
  }
  function updateClaim(claimId: string, updatedClaim: Partial<Claim>) {
    setClaims((prev) =>
      prev.map((claim) =>
        claim.id === claimId ? { ...claim, ...updatedClaim } : claim
      )
    );
  }
  function updateClaimDetail(
    detailId: string,
    updatedDetail: Partial<ClaimDetail>
  ) {
    setClaimDetails((prev) =>
      prev.map((detail) =>
        detail.id === detailId ? { ...detail, ...updatedDetail } : detail
      )
    );
  }

  function addInvoice(invoice: Invoice) {
    setInvoices((prev) => [...prev, invoice]);
  }

  function updateInvoice(invoiceId: string, updatedInvoice: Partial<Invoice>) {
    setInvoices((prev) =>
      prev.map((invoice) =>
        invoice.loads[0].id === invoiceId
          ? { ...invoice, ...updatedInvoice }
          : invoice
      )
    );
  }

  return (
    <DemoData.Provider
      value={{
        loads,
        carriers,
        trucks,
        invoices,
        drivers,
        claims,
        claimDetails,
        accountType,
        setAccountType,
        addDriver,
        updateDriver,
        addLoad,
        addCarrier,
        addTruck,
        addClaim,
        addClaimDetails: addClaimDetail,
        updateLoad,
        updateCarrier,
        updateTruck,
        updateClaim,
        updateClaimDetails: updateClaimDetail,
        addInvoice,
        updateInvoice,
      }}
    >
      {children}
    </DemoData.Provider>
  );
}
